import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronLeft, Calendar, RefreshCw, Loader2, Pencil, Plus, Minus,
  Trash2, Package, Search, CheckCircle2, Clock, X, FileText, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";
import { printHTML } from "../../utils/printHTML";
import { buildBulkPrintHtml } from "../admin/BulkConfirmPrintButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const splitDate = (iso) => {
  if (!iso) return { day: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString("en-GB", { year: "2-digit", month: "2-digit", day: "2-digit" }),
      time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch { return { day: iso, time: "" }; }
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

// ─── Item Editor Modal ───
// المشرف يقدر فقط: يضيف مادة / يشيل مادة / يعدل كمية. ما يقدر يغيّر السعر.
const EditModal = ({ order, products, onClose, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [q, setQ] = useState("");
  const [local, setLocal] = useState(order);

  const filtered = useMemo(() => {
    if (!q.trim()) return (products || []).slice(0, 20);
    return (products || []).filter((p) =>
      (p.name_ar || "").includes(q) || (p.name || "").includes(q)
    ).slice(0, 40);
  }, [products, q]);

  const patch = async (action, product_id, quantity) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/supervisor/my-sales/${order.id}/items`,
        { action, product_id, quantity },
      );
      // Optimistic reload of local
      const { data: refresh } = await axios.get(`${API}/supervisor/my-sales`);
      const updated = (refresh?.sales || []).find((s) => s.id === order.id);
      if (updated) setLocal(updated);
      toast.success(
        action === "remove" ? "تم حذف المادة"
        : action === "add" ? "تم إضافة المادة"
        : "تم تعديل الكمية"
      );
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التعديل");
    } finally { setBusy(false); }
  };

  const items = local.items || [];

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="sup-edit-modal">
        {/* Header */}
        <div className="p-3 border-b border-[#E4EBE6] bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-t-2xl flex items-center gap-2">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
            <Pencil className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-black text-sm">تعديل فاتورة #{local.invoice_number}</p>
            <p className="text-[10px] opacity-90 truncate">{local.user_name} · {fmt(local.total)} د.ع</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="sup-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info banner */}
        <div className="px-3 py-2 bg-[#FEF3C7] text-[10px] text-[#B45309] border-b border-[#F5A623]/30">
          ⚠ الصلاحيات: إضافة/حذف مواد + تغيير الكمية فقط. لا يمكن تغيير السعر أو اسم الزبون.
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {items.length === 0 ? (
            <p className="text-center py-4 text-xs text-[#5C6E64]">لا توجد بنود</p>
          ) : items.map((it) => (
            <div key={it.product_id} className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-xl p-2 flex items-center gap-2" data-testid={`sup-edit-item-${it.product_id}`}>
              {it.image_path ? (
                <img src={`${API}/files/${it.image_path}`} alt="" className="w-11 h-11 rounded-lg object-cover" />
              ) : (
                <div className="w-11 h-11 bg-white rounded-lg flex items-center justify-center"><Package className="w-4 h-4 text-[#5C6E64]" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-xs truncate">{it.name_ar}</p>
                <p className="text-[10px] text-[#5C6E64]" dir="ltr">
                  {fmt(it.price)} × {it.quantity} = <b>{fmt(it.price * it.quantity)}</b>
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => patch("set_qty", it.product_id, Number(it.quantity) - 1)}
                  disabled={busy}
                  data-testid={`sup-edit-minus-${it.product_id}`}
                  className="bg-[#FEE2E2] hover:bg-[#FECACA] text-[#B91C1C] w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-50"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="font-black w-8 text-center text-sm" dir="ltr">{it.quantity}</span>
                <button
                  onClick={() => patch("set_qty", it.product_id, Number(it.quantity) + 1)}
                  disabled={busy}
                  data-testid={`sup-edit-plus-${it.product_id}`}
                  className="bg-[#DCFCE7] hover:bg-[#BBF7D0] text-[#15803D] w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-50"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { if (window.confirm("حذف هذه المادة من الفاتورة؟")) patch("remove", it.product_id, 0); }}
                  disabled={busy}
                  data-testid={`sup-edit-del-${it.product_id}`}
                  className="bg-[#E85D45] hover:bg-[#c14a36] text-white w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-50 mr-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add product picker */}
        <div className="border-t border-[#E4EBE6] p-2">
          {!showPicker ? (
            <button onClick={() => setShowPicker(true)} data-testid="sup-edit-add-btn"
              className="w-full bg-[#15803D] hover:bg-[#126b32] text-white rounded-xl py-2 font-bold text-sm flex items-center justify-center gap-1">
              <Plus className="w-4 h-4" /> إضافة مادة جديدة
            </button>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
                <input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="ابحث عن مادة..." autoFocus
                  data-testid="sup-edit-picker-search"
                  className="w-full pr-9 pl-3 py-2 rounded-xl border-2 border-[#15803D]/50 text-sm"
                />
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { patch("add", p.id, 1); setShowPicker(false); setQ(""); }}
                    disabled={busy}
                    data-testid={`sup-edit-add-${p.id}`}
                    className="w-full flex items-center gap-2 bg-white hover:bg-[#F0FDF4] border border-[#E4EBE6] rounded-lg p-2 text-right"
                  >
                    {p.image_path && <img src={`${API}/files/${p.image_path}`} alt="" className="w-9 h-9 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{p.name_ar}</p>
                      <p className="text-[10px] text-[#5C6E64]" dir="ltr">{fmt(p.price)} د.ع · مخزن {p.stock}</p>
                    </div>
                    <Plus className="w-4 h-4 text-[#15803D]" />
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p className="text-center py-2 text-xs text-[#5C6E64]">لا نتائج</p>
                )}
              </div>
              <button onClick={() => { setShowPicker(false); setQ(""); }}
                className="w-full bg-[#F3F6F4] text-[#5C6E64] rounded-xl py-1.5 text-xs font-bold">إلغاء</button>
            </div>
          )}
        </div>

        {/* Footer summary */}
        <div className="p-3 border-t border-[#E4EBE6] bg-[#F8FAFC] flex items-center justify-between">
          <p className="text-[10px] text-[#5C6E64]">
            المجموع الجديد
          </p>
          <p className="font-black text-lg text-[#1E3A5F]" dir="ltr">{fmt(local.total)} د.ع</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main list ───
const waText = (o) => {
  const lines = [`فاتورة جُملتي #${o.invoice_number}`, `الزبون: ${o.user_name || ""}`, "───────────"];
  (o.items || []).forEach((it) => lines.push(`• ${it.name_ar} — ${it.quantity} × ${fmt(it.price)} = ${fmt(it.price * it.quantity)}`));
  lines.push("───────────", `💰 الإجمالي: ${fmt(o.total)} د.ع`, "", "شكراً لاختياركم جُملتي 🛒");
  return lines.join("\n");
};
const sendWa = (o) => {
  let ph = String(o.user_phone || "").replace(/\D/g, "");
  if (ph.startsWith("964")) ph = ph.slice(3); else if (ph.startsWith("0")) ph = ph.slice(1);
  window.open(`https://api.whatsapp.com/send?phone=964${ph}&text=${encodeURIComponent(waText(o))}`, "_blank");
};

const MySalesList = ({ onBack, canDelete = false }) => {
  const [from, setFrom] = useState(daysAgoISO(6));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [products, setProducts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/supervisor/my-sales`, { params: { date_from: from, date_to: to } });
      setData(res);
    } catch (e) {
      toast.error("فشل تحميل القوائم");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/supervisor/products`);
        setProducts(data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const [search, setSearch] = useState("");
  const sales = (data?.sales || []).filter((o) => {
    const t = search.trim();
    return !t || smartMatch(`${o.user_name || ""} ${o.market_name || ""} ${o.user_phone || ""} ${o.invoice_number || ""}`, t);
  });
  const removeSale = async (o) => {
    if (!window.confirm(`حذف الفاتورة #${o.invoice_number} كاملة؟ ترجع المواد للمخزن وينشال الدين.`)) return;
    try {
      await axios.delete(`${API}/supervisor/my-sales/${o.id}`);
      toast.success("تم حذف الفاتورة");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحذف"); }
  };

  return (
    <div className="space-y-3" data-testid="sup-my-sales">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button onClick={onBack} data-testid="sup-mysales-back"
          className="bg-white border border-[#E4EBE6] rounded-xl p-2 hover:bg-[#F3F6F4]">
          <ChevronLeft className="w-4 h-4 rotate-180" />
        </button>
        <h2 className="font-black text-lg text-[#1E3A5F] flex-1">📋 قوائمي السابقة</h2>
        <button onClick={load} data-testid="sup-mysales-refresh"
          className="bg-[#1E3A5F] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> تحديث
        </button>
      </div>

      {/* Date filter */}
      <div className="bg-white border border-[#E4EBE6] rounded-2xl p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[130px]">
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="sup-from"
              className="w-full pr-9 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 text-sm" />
          </div>
          <span className="font-bold text-[#5C6E64]">→</span>
          <div className="relative flex-1 min-w-[130px]">
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="sup-to"
              className="w-full pr-9 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 text-sm" />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          <QuickBtn label="اليوم" onClick={() => { setFrom(todayISO()); setTo(todayISO()); }} />
          <QuickBtn label="أمس" onClick={() => { const d = daysAgoISO(1); setFrom(d); setTo(d); }} />
          <QuickBtn label="آخر ٧" onClick={() => { setFrom(daysAgoISO(6)); setTo(todayISO()); }} />
          <QuickBtn label="آخر ٣٠" onClick={() => { setFrom(daysAgoISO(29)); setTo(todayISO()); }} />
        </div>
      </div>

      <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sup-mysales-search"
        placeholder="ابحث باسم الزبون أو الماركت أو رقم الفاتورة..."
        className="w-full px-4 py-2.5 rounded-2xl border-2 border-[#E4EBE6] focus:border-[#1E3A5F] outline-none text-sm bg-white" />

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-3 gap-2">
          <StatCard label="مجموع القوائم" val={fmt(data.count)} color="from-[#1E3A5F] to-[#3B82F6]" />
          <StatCard label="قابلة للتعديل" val={fmt(data.editable_count)} color="from-[#F5A623] to-[#dc9320]" />
          <StatCard label="إجمالي المبيعات" val={fmt(sales.reduce((s, o) => s + Number(o.total || 0), 0))} color="from-[#15803D] to-[#22C55E]" />
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-7 h-7 animate-spin inline text-[#1E3A5F]" /></div>
      ) : sales.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#E4EBE6] rounded-2xl p-8 text-center text-[#5C6E64]">
          <p className="font-bold text-sm">لا توجد قوائم في هذه الفترة</p>
        </div>
      ) : sales.map((o) => {
        const dt = splitDate(o.created_at);
        const editable = o.can_edit;
        return (
          <div key={o.id} data-testid={`sup-sale-${o.id}`}
            className={`bg-white rounded-2xl p-3 border-2 ${editable ? "border-[#F5A623]" : "border-[#E4EBE6]"}`}>
            <div className="flex items-start gap-2">
              <div className="bg-[#1E3A5F] text-white rounded-lg px-2 py-1 text-center shrink-0" style={{minWidth: 52}}>
                <p className="text-[9px] opacity-80 leading-none">قائمة</p>
                <p className="text-sm font-black leading-none mt-0.5" dir="ltr">#{o.invoice_number}</p>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="font-bold text-sm text-[#111814] truncate">{o.user_name}</p>
                  {editable ? (
                    <span className="text-[9px] bg-[#FEF3C7] text-[#B45309] font-bold rounded-full px-2 py-0.5 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> بانتظار تأكيد الإدارة
                    </span>
                  ) : (
                    <span className="text-[9px] bg-[#DCFCE7] text-[#15803D] font-bold rounded-full px-2 py-0.5 flex items-center gap-1">
                      <CheckCircle2 className="w-2.5 h-2.5" /> مؤكّدة
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-[#5C6E64] truncate">
                  {o.market_name && <>🏪 {o.market_name} · </>}
                  <span dir="ltr">{o.user_phone}</span>
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px]">
                  <span className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-full px-2 py-0.5" dir="ltr">
                    📅 {dt.day} · {dt.time}
                  </span>
                  <span className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-full px-2 py-0.5">
                    <Package className="w-2.5 h-2.5 inline" /> <span dir="ltr">{o.items_count}</span> صنف
                  </span>
                  {o.merged_count > 0 && (
                    <span className="bg-[#DBEAFE] text-[#1E40AF] rounded-full px-2 py-0.5 font-bold">
                      🔀 مدموجة (<span dir="ltr">{o.merged_count}</span>)
                    </span>
                  )}
                </div>
              </div>
              <div className="text-left shrink-0">
                <p className="text-[9px] text-[#5C6E64]">الإجمالي</p>
                <p className="font-black text-base text-[#1E3A5F]" dir="ltr">{fmt(o.total)}</p>
              </div>
            </div>
            {editable && (
              <button
                onClick={() => setEditingOrder(o)}
                data-testid={`sup-edit-${o.id}`}
                className="w-full mt-2 bg-gradient-to-l from-[#F5A623] to-[#dc9320] hover:from-[#dc9320] hover:to-[#c48420] text-white font-bold rounded-xl py-2 text-sm flex items-center justify-center gap-1"
              >
                <Pencil className="w-3.5 h-3.5" /> تعديل البنود
              </button>
            )}
            <div className="grid grid-cols-3 gap-1.5 mt-2">
              <button onClick={() => printHTML(buildBulkPrintHtml([o]), { title: `فاتورة ${o.invoice_number}` })}
                data-testid={`sup-pdf-${o.id}`} className="bg-[#1E3A5F] text-white text-xs font-bold rounded-xl py-2 flex items-center justify-center gap-1">
                <FileText className="w-3.5 h-3.5" /> PDF
              </button>
              <button onClick={() => sendWa(o)} data-testid={`sup-wa-${o.id}`}
                className="bg-[#16A34A] text-white text-xs font-bold rounded-xl py-2 flex items-center justify-center gap-1">
                <Share2 className="w-3.5 h-3.5" /> واتساب
              </button>
              {canDelete && editable ? (
                <button onClick={() => removeSale(o)} data-testid={`sup-delete-${o.id}`}
                  className="bg-red-600 text-white text-xs font-bold rounded-xl py-2 flex items-center justify-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> حذف
                </button>
              ) : <span />}
            </div>
          </div>
        );
      })}

      {/* Edit modal */}
      {editingOrder && (
        <EditModal
          order={editingOrder}
          products={products}
          onClose={() => setEditingOrder(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
};

const QuickBtn = ({ label, onClick }) => (
  <button onClick={onClick} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] rounded-full px-2 py-1 font-bold">
    {label}
  </button>
);

const StatCard = ({ label, val, color }) => (
  <div className={`bg-gradient-to-l ${color} text-white rounded-xl p-2 text-center`}>
    <p className="text-[9px] opacity-90">{label}</p>
    <p className="text-base font-black mt-0.5" dir="ltr">{val}</p>
  </div>
);

export default MySalesList;
